import { z } from 'zod';
import { isoDateTime, mediumText, publishStatus, shortText, slug, uuid } from './primitives.js';
import { guidePermissionSet } from './policy.js';
/**
 * Guides: a platform-wide knowledge base, organised into sections the way a
 * documentation site is. Every active user reads published guides — they are
 * not course-scoped and not enrollment-gated. Trainers and admins write them.
 *
 * A guide carries two copies of its words. The working copy is what the editor
 * autosaves; the published copy is what readers see. Publishing copies the
 * working copy across, so editing a live guide never shows readers a sentence
 * that is half typed.
 */
/** Search results mark the matched words between these two characters. They
 *  are control characters that cannot appear in a guide, so the client can
 *  split on them and render <mark> itself — the API never returns HTML. */
export const SEARCH_MARK_START = '\u0002';
export const SEARCH_MARK_END = '\u0003';
export const guideTitle = z
    .string()
    .trim()
    .min(1, 'Give the guide a title.')
    .max(160, 'Keep the title under 160 characters.');
export const guideSectionTitle = z
    .string()
    .trim()
    .min(1, 'Give the section a title.')
    .max(80, 'Keep section titles under 80 characters.');
/** Guides are reference pages and run far longer than a lesson — a full skill
 *  handbook is ~210k characters. Kept under the API's 2 MB request limit even
 *  if every character were four bytes. */
export const GUIDE_BODY_MAX = 400_000;
export const guideBody = z
    .string()
    .max(GUIDE_BODY_MAX, `Keep the guide under ${GUIDE_BODY_MAX.toLocaleString('en-US')} characters.`);
export const guidePerson = z.object({ id: uuid, name: z.string() });
// ------------------------------------------------------------------ reader ---
/** One entry in the guides navigation. */
export const guideNavItem = z.object({
    id: uuid,
    slug: z.string(),
    title: z.string(),
    summary: z.string(),
    /** `draft` only ever reaches someone who may edit it; readers never see one. */
    status: publishStatus,
    updatedAt: isoDateTime,
});
export const guideNavSection = z.object({
    id: uuid,
    slug: z.string(),
    title: z.string(),
    description: z.string(),
    guides: z.array(guideNavItem),
});
/** GET /api/guides — the whole navigation tree, which is small. */
export const guidesIndexResponse = z.object({
    sections: z.array(guideNavSection),
    /** Whether "Write a guide" is available, and if not, why — rendered locked. */
    create: z.object({
        allowed: z.boolean(),
        reason: z.string().nullable(),
    }),
});
// -------------------------------------------------------------------- apps ---
/**
 * An interactive app in a guide: a standalone HTML page, or a zip of an app
 * that is already built, run in a sandboxed frame from the sandbox origin.
 *
 * It is placed in the Markdown with a fenced block in the `app` language that
 * names the upload:
 *
 *     ```app
 *     3f9c2a1e-… height=640
 *     ```
 *
 * The frame's address always comes from the API, for an app that belongs to
 * the guide — never from anything the author typed — so the block can do no
 * more than pick one of the guide's own uploads.
 */
export const GUIDE_APP_LANGUAGE = 'app';
export const GUIDE_APP_HEIGHT = { min: 240, max: 1600, default: 560 };
const UUID_IN_TEXT = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
/** Read an ```app block's contents: the upload it names, and the frame height. */
export function parseAppBlock(source) {
    const id = UUID_IN_TEXT.exec(source)?.[0]?.toLowerCase();
    if (!id)
        return null;
    const asked = /\bheight\s*[=:]\s*(\d{1,5})\b/i.exec(source);
    const height = asked
        ? Math.min(GUIDE_APP_HEIGHT.max, Math.max(GUIDE_APP_HEIGHT.min, Number(asked[1])))
        : GUIDE_APP_HEIGHT.default;
    return { id, height };
}
/** The Markdown that places an app. */
export function appBlock(id, height = GUIDE_APP_HEIGHT.default) {
    const suffix = height === GUIDE_APP_HEIGHT.default ? '' : ` height=${height}`;
    return `\`\`\`${GUIDE_APP_LANGUAGE}\n${id}${suffix}\n\`\`\``;
}
/** What a reader's page needs to open one app: its signed frame link. */
export const guideAppFrame = z.object({
    id: uuid,
    title: z.string(),
    /** On the sandbox origin; lapses after a few hours, so a reload mints another. */
    url: z.string(),
});
export const guideAppFormat = z.enum(['html', 'zip']);
/** One upload, as the editor lists it. */
export const guideApp = guideAppFrame.extend({
    format: guideAppFormat,
    fileName: z.string(),
    fileSize: z.number().int(),
    fileCount: z.number().int(),
    /** The page that opens the app, inside the upload. */
    entryPath: z.string(),
    warnings: z.array(z.string()),
    /** Named in the working copy — what the editor shows. */
    inWorkingCopy: z.boolean(),
    /** Named in the published copy — what readers have open. */
    inPublishedCopy: z.boolean(),
    createdAt: isoDateTime,
    createdBy: guidePerson.nullable(),
});
/** Text fields sent beside the file on POST /api/admin/guides/:guideId/apps. */
export const uploadGuideAppFields = z.object({
    title: z.string().trim().max(160).optional(),
    /** Overrides the page the app opens on, inside a zip. */
    entryPath: z.string().trim().max(512).optional(),
});
export const guideLink = z.object({
    sectionSlug: z.string(),
    slug: z.string(),
    title: z.string(),
});
export const guideArticle = z.object({
    id: uuid,
    slug: z.string(),
    title: z.string(),
    summary: z.string(),
    bodyMd: z.string(),
    status: publishStatus,
    hasUnpublishedChanges: z.boolean(),
    publishedAt: isoDateTime.nullable(),
    updatedAt: isoDateTime,
    author: guidePerson.nullable(),
    /** Who last published what the reader is looking at. */
    publishedBy: guidePerson.nullable(),
});
/** GET /api/guides/:sectionSlug/:guideSlug */
export const guideReadResponse = z.object({
    section: z.object({ id: uuid, slug: z.string(), title: z.string() }),
    guide: guideArticle,
    /**
     * True when the caller is seeing the working copy of a guide that has never
     * been published — an author's preview. Readers never get one.
     */
    preview: z.boolean(),
    previous: guideLink.nullable(),
    next: guideLink.nullable(),
    permissions: guidePermissionSet,
    /** Frames for the apps the body places, and only those. */
    apps: z.array(guideAppFrame),
});
export const guideSearchQuery = z.object({
    q: z.string().trim().min(2, 'Type at least two characters.').max(200),
});
export const guideSearchResult = z.object({
    id: uuid,
    sectionSlug: z.string(),
    sectionTitle: z.string(),
    slug: z.string(),
    title: z.string(),
    summary: z.string(),
    /** A fragment of the body, matched words between SEARCH_MARK_START/END. */
    snippet: z.string(),
});
/** GET /api/guides/search?q= */
export const guideSearchResponse = z.object({
    query: z.string(),
    results: z.array(guideSearchResult),
});
// ----------------------------------------------------------------- console ---
export const adminGuideRow = z.object({
    id: uuid,
    sectionId: uuid,
    slug: z.string(),
    title: z.string(),
    status: publishStatus,
    hasUnpublishedChanges: z.boolean(),
    author: guidePerson.nullable(),
    updatedAt: isoDateTime,
    publishedAt: isoDateTime.nullable(),
    permissions: guidePermissionSet,
});
export const adminGuideSection = z.object({
    id: uuid,
    slug: z.string(),
    title: z.string(),
    description: z.string(),
    orderIndex: z.number().int(),
    guides: z.array(adminGuideRow),
});
/** GET /api/admin/guides */
export const adminGuidesResponse = z.object({
    sections: z.array(adminGuideSection),
    create: z.object({ allowed: z.boolean(), reason: z.string().nullable() }),
    manageSections: z.object({ allowed: z.boolean(), reason: z.string().nullable() }),
});
export const guideEditorGuide = z.object({
    id: uuid,
    sectionId: uuid,
    slug: z.string(),
    title: z.string(),
    summary: z.string(),
    bodyMd: z.string(),
    status: publishStatus,
    hasUnpublishedChanges: z.boolean(),
    /** Never published: the working copy is all there is. */
    neverPublished: z.boolean(),
    publishedAt: isoDateTime.nullable(),
    updatedAt: isoDateTime,
    author: guidePerson.nullable(),
    updatedBy: guidePerson.nullable(),
});
/** GET /api/admin/guides/:guideId — and the answer to every editor mutation. */
export const guideEditorResponse = z.object({
    guide: guideEditorGuide,
    section: z.object({ id: uuid, slug: z.string(), title: z.string() }),
    sections: z.array(z.object({ id: uuid, title: z.string() })),
    permissions: guidePermissionSet,
    /** Every app uploaded to this guide, newest first. */
    apps: z.array(guideApp),
});
/** POST /api/admin/guides */
export const createGuideRequest = z.object({
    sectionId: uuid,
    title: guideTitle,
});
/** PATCH /api/admin/guides/:guideId — every field optional; autosave sends one. */
export const updateGuideRequest = z
    .object({
    title: guideTitle.optional(),
    summary: mediumText.optional(),
    bodyMd: guideBody.optional(),
    slug: slug.optional(),
    sectionId: uuid.optional(),
})
    .refine((v) => Object.keys(v).length > 0, 'Nothing to change.');
/** POST /api/admin/guide-sections */
export const createGuideSectionRequest = z.object({
    title: guideSectionTitle,
    description: shortText.default(''),
});
/** PATCH /api/admin/guide-sections/:sectionId */
export const updateGuideSectionRequest = z
    .object({
    title: guideSectionTitle.optional(),
    description: shortText.optional(),
    slug: slug.optional(),
})
    .refine((v) => Object.keys(v).length > 0, 'Nothing to change.');
//# sourceMappingURL=guides.js.map